import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { IconComponent } from 'src/app/ui-kit/icon/icon.component';

/** Emitted payload when the paginator state changes. */
export interface PageChangeEvent {
  page: number;
  pageSize: number;
}

/**
 * Generic paginator. Pairs with `DataTableComponent` or any list. Responsive: on narrow
 * screens collapses to prev/next + a compact "X of Y" label.
 *
 * Pages are 0-indexed internally and displayed as 1-indexed to the user.
 *
 * @example
 * <app-paginator
 *   [page]="page()"
 *   [pageSize]="pageSize()"
 *   [total]="total()"
 *   [pageSizeOptions]="[10, 25, 50]"
 *   (pageChange)="onPageChange($event)"
 * />
 *
 * @feature shared
 */
@Component({
  selector: 'app-paginator',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  host: { class: 'block' },
  template: `
    <div class="flex flex-wrap items-center justify-between gap-3 text-sm text-muted">
      <div class="flex items-center gap-2">
        @if (pageSizeOptions().length) {
          <label class="flex items-center gap-2">
            <span>Rows per page:</span>
            <select
              class="rounded border border-outline bg-surface px-2 py-1 text-body focus-visible:outline-none focus-visible:shadow-[var(--theme-focus-ring)]"
              [value]="pageSize()"
              (change)="onPageSize($event)"
            >
              @for (size of pageSizeOptions(); track size) {
                <option [value]="size">{{ size }}</option>
              }
            </select>
          </label>
        }
      </div>

      <div class="flex items-center gap-2">
        <span>{{ rangeLabel() }}</span>

        <button
          type="button"
          class="inline-flex items-center justify-center rounded-full p-2 text-muted hover:text-body disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:shadow-[var(--theme-focus-ring)]"
          aria-label="First page"
          [disabled]="isFirstPage()"
          (click)="goTo(0)"
        >
          <app-icon name="chevron-back" size="xs" />
          <app-icon name="chevron-back" size="xs" />
        </button>

        <button
          type="button"
          class="inline-flex items-center justify-center rounded-full p-2 text-muted hover:text-body disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:shadow-[var(--theme-focus-ring)]"
          aria-label="Previous page"
          [disabled]="isFirstPage()"
          (click)="goTo(page() - 1)"
        >
          <app-icon name="chevron-back" size="xs" />
        </button>

        <button
          type="button"
          class="inline-flex items-center justify-center rounded-full p-2 text-muted hover:text-body disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:shadow-[var(--theme-focus-ring)]"
          aria-label="Next page"
          [disabled]="isLastPage()"
          (click)="goTo(page() + 1)"
        >
          <app-icon name="chevron-forward" size="xs" />
        </button>

        <button
          type="button"
          class="inline-flex items-center justify-center rounded-full p-2 text-muted hover:text-body disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:shadow-[var(--theme-focus-ring)]"
          aria-label="Last page"
          [disabled]="isLastPage()"
          (click)="goTo(lastPage())"
        >
          <app-icon name="chevron-forward" size="xs" />
          <app-icon name="chevron-forward" size="xs" />
        </button>
      </div>
    </div>
  `,
})
export class PaginatorComponent {
  /** Current 0-indexed page. @default 0 */
  readonly page = input<number>(0);

  /** Current page size. @default 25 */
  readonly pageSize = input<number>(25);

  /** Total number of items. @default 0 */
  readonly total = input<number>(0);

  /** Options offered in the page-size select. Pass `[]` to hide the select. @default [10, 25, 50, 100] */
  readonly pageSizeOptions = input<number[]>([10, 25, 50, 100]);

  /** Fires when page or pageSize changes. */
  readonly pageChange = output<PageChangeEvent>();

  /** Zero-indexed last page. */
  readonly lastPage = computed(() => {
    const ps = Math.max(1, this.pageSize());
    return Math.max(0, Math.ceil(this.total() / ps) - 1);
  });

  readonly isFirstPage = computed(() => this.page() <= 0);
  readonly isLastPage = computed(() => this.page() >= this.lastPage());

  /** Label shown in the middle: "1–25 of 237". */
  readonly rangeLabel = computed(() => {
    const total = this.total();
    if (total === 0) return '0 of 0';
    const start = this.page() * this.pageSize() + 1;
    const end = Math.min(total, (this.page() + 1) * this.pageSize());
    return `${start}–${end} of ${total}`;
  });

  goTo(page: number): void {
    const clamped = Math.max(0, Math.min(page, this.lastPage()));
    if (clamped === this.page()) return;
    this.pageChange.emit({ page: clamped, pageSize: this.pageSize() });
  }

  onPageSize(event: Event): void {
    const size = Number((event.target as HTMLSelectElement).value);
    if (!Number.isFinite(size) || size <= 0) return;
    const firstVisible = this.page() * this.pageSize();
    const newPage = Math.floor(firstVisible / size);
    this.pageChange.emit({ page: newPage, pageSize: size });
  }
}
